import { searchBoxFlights as e2eClasses } from 'constants/e2eClasses'
import { TRIP_TYPES } from 'constants/tripTypes'
import { generateFlexCalendarRoute, generateFlightOffersRoute } from 'helpers/url/localization'
import { isEmpty } from 'helpers/utils'

export const getFlightTypesRadioGroupOptions = (
  campaignFlightTypes,
  translation,
  stateFlightType
) => {
  let defaultKeys = [
    {
      value: TRIP_TYPES.ONE_WAY,
      text: translation('label.one-way-trip'),
      e2eClass: e2eClasses.from
    },
    {
      value: TRIP_TYPES.ROUND_TRIP,
      text: translation('label.round-trip'),
      e2eClass: e2eClasses.fromTo
    },
    {
      value: TRIP_TYPES.MULTIDESTINY,
      text: translation('label.multidestiny-trip'),
      e2eClass: e2eClasses.multidestiny
    }
  ]

  const keys =
    (!isEmpty(campaignFlightTypes) &&
      defaultKeys.filter(keyItem => campaignFlightTypes.includes(keyItem.value))) ||
    defaultKeys

  return keys.map(item => ({
    value: item.value,
    text: item.text,
    e2eClass: item.e2eClass,
    selected: stateFlightType ? stateFlightType === item.value : false
  }))
}

export const getConfigPassengersInputNumberValue = (translation, passengersList) => {
  const keys = [
    {
      title: translation('label.adult', { count: 2 }),
      age: translation('shopping.search-box.config-passenger.date-range-adult'),
      id: 'adt'
    },
    {
      title: translation('label.child', { count: 2 }),
      age: translation('shopping.search-box.config-passenger.date-range-children'),
      id: 'chd'
    },
    {
      title: translation('label.infant', { count: 2 }),
      age: translation('shopping.search-box.config-passenger.date-range-infant'),
      id: 'inf'
    }
  ]

  return keys.map(({ id, title, age }, index) => ({
    id,
    title,
    age,
    value: passengersList
      ? passengersList.passengers[index]
      : passengersList.passengers[index] === 0 && id === 'adt'
      ? null
      : 0
  }))
}

const filterCabinsByRules = (cabins, availableCabins) => {
  if (isEmpty(availableCabins)) {
    return cabins
  }
  //Workaround campaign uppercase codes
  return cabins.filter(({ code }) => availableCabins.includes(code || code.toUpperCase()))
}

export const getConfigPassengerRadioGroupOptions = (
  cabins,
  campaignPropsAvailableCabins,
  availableCabins,
  passengersList
) => {
  const restrictedCabins = campaignPropsAvailableCabins || availableCabins

  return filterCabinsByRules(cabins, restrictedCabins).map(item => ({
    value: item.code,
    text: item.name,
    e2eClass: item.code,
    key: item.code,
    selected: passengersList ? passengersList.radioGroupValue === item.code : false
  }))
}

export const getCampaignConstraints = (group, campaignProps) => {
  let countries = ''
  let regions = ''
  let cities = ''

  if (campaignProps) {
    if (group === 'suggestionsFrom') {
      countries = campaignProps.availableCountryOrigins
      regions = campaignProps.availableRegionOrigins
      cities = campaignProps.availableOrigins
    } else {
      countries = campaignProps.availableCountryDestinations
      regions = campaignProps.availableRegionDestinations
      cities = campaignProps.availableDestinations
    }
  }
  return { countries, regions, cities }
}
const SEARCH_HISTORY_LIMIT = 3

export const saveRecentSearchs = (key, value) => {
  let data = JSON.parse(localStorage.getItem(key))
  if (!data) {
    data = { history: [] }
  }
  const alreadyInHistory = data.history.find(el => el.id === value.id)

  if (!alreadyInHistory) {
    data.history.length > SEARCH_HISTORY_LIMIT && data.history.pop()
    data.history.unshift({
      id: value.id,
      display: value.display,
      highlight: value.display,
      cityCode: value.cityCode
    })
  }

  localStorage.setItem(key, JSON.stringify(data))
}

export const createEmptyLeg = () => ({
  from: undefined,
  to: undefined,
  fromSelected: undefined,
  toSelected: undefined,
  date: undefined,
  isExpanded: true
})

export const updateFromCampaign = (
  campaignProps,
  passengers,
  campaignFieldsInitialValues,
  currentLegs
) => {
  const {
    flightTypes,
    availableCabins,
    availablePassengers,
    validationFields,
    isConference,
    flexDatesAllowed,
    promoCodeEnabled
  } = campaignProps

  // Same with all needed to set state
  const nextState = {}
  if (isConference) {
    nextState.conferenceCode = { value: '' }
    //if conference is changed data input by user should be cleared
    nextState.legs = [createEmptyLeg()]
    nextState.passengers = passengers
  }
  if (!flexDatesAllowed) {
    nextState.flexDates = false
  }
  nextState.promoCodeEnabled = promoCodeEnabled

  if (
    flightTypes &&
    currentLegs.length === 1 &&
    nextState.flightTypes === TRIP_TYPES.MULTIDESTINY
  ) {
    nextState.legs = [...currentLegs, createEmptyLeg()]
  }

  if (availableCabins) {
    nextState.availableCabins = availableCabins
  }
  if (availablePassengers) {
    const FIRST_VALUE_INDEX = 0
    const ENTRY_VALUE_INDEX = 1
    const iterableConstraints = Object.entries(availablePassengers)

    nextState.passengers = {}
    nextState.passengers.passengers = []

    passengers &&
      (passengers.passengers || []).forEach((value, index) => {
        if (!isEmpty(iterableConstraints[index][ENTRY_VALUE_INDEX])) {
          nextState.passengers.passengers.push(
            iterableConstraints[index][ENTRY_VALUE_INDEX][FIRST_VALUE_INDEX] || 0
          )
        } else {
          nextState.passengers.passengers.push(value)
        }
      })
  }

  if (validationFields) {
    const conditionalFields = {}
    validationFields.forEach(fieldData => {
      conditionalFields[fieldData.code] = {
        placeholder: fieldData.placeholder,
        value:
          (!isEmpty(campaignFieldsInitialValues) &&
            campaignFieldsInitialValues[fieldData.code].value) ||
          ''
      }
    })
    nextState.conditionalFields = conditionalFields
  }

  return nextState
}

export const getDateLimitForMulti = (legIndex, legs) => {
  const PREV = legIndex - 1

  for (let i = PREV; i >= 0; i--) {
    if (legs[i].date) {
      return legs[i].date.from /* get first valid date from any previous leg */
    }
  }
}

export const getPathName = (isFlexDates, language) =>
  isFlexDates ? generateFlexCalendarRoute(language) : generateFlightOffersRoute(language)
